import { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { X } from "lucide-react";
import { Button } from "../../../components/ui/Button";
import { PAGE_LABELS, PAGE_CATEGORIES } from "../../../types/pages";
import type { PageKey } from "../../../types/pages";
import type { Role } from "../../../types/index";

const roleSchema = z.object({
  name: z.string().min(1, "Name is required").max(100),
  description: z.string().max(500).optional(),
  pages: z.array(z.string()).min(1, "Select at least one page"),
});

type RoleFormValues = z.infer<typeof roleSchema>;

interface RoleFormDrawerProps {
  role?: Role;
  onClose: () => void;
  onSubmit: (values: { name: string; description?: string; pages: PageKey[] }) => Promise<void>;
}

const IC = "w-full rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm text-[#0f172a] placeholder:text-[#94a3b8] focus:outline-none focus:ring-2 focus:ring-[#2447d9] focus:border-transparent";

export function RoleFormDrawer({ role, onClose, onSubmit }: RoleFormDrawerProps) {
  const isEdit = !!role;

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<RoleFormValues>({
    resolver: zodResolver(roleSchema),
    defaultValues: {
      name: role?.name ?? "",
      description: role?.description ?? "",
      pages: role?.pages ?? [],
    },
  });

  useEffect(() => {
    reset({
      name: role?.name ?? "",
      description: role?.description ?? "",
      pages: role?.pages ?? [],
    });
  }, [role, reset]);

  const handleFormSubmit = async (values: RoleFormValues) => {
    await onSubmit({
      name: values.name,
      description: values.description,
      pages: values.pages as PageKey[],
    });
  };

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <aside
        className="fixed inset-y-0 right-0 z-50 w-full max-w-md bg-white shadow-2xl flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-label={isEdit ? "Edit Role" : "Create Role"}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[#e2e8f0] px-6 py-4">
          <h2 className="text-base font-semibold text-[#0f172a]">
            {isEdit ? "Edit Role" : "Create Role"}
          </h2>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-[#64748b] hover:bg-[#f1f5f9] hover:text-[#0f172a]"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit(handleFormSubmit)}
          className="flex flex-col flex-1 overflow-hidden"
        >
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
            {/* Name */}
            <div>
              <label htmlFor="role-name" className="block text-sm font-medium text-[#0f172a] mb-1">
                Role Name <span className="text-[#dc2626]">*</span>
              </label>
              <input
                id="role-name"
                {...register("name")}
                className={IC}
                placeholder="e.g. Store Supervisor"
              />
              {errors.name && (
                <p className="mt-1 text-xs text-[#dc2626]">{errors.name.message}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label htmlFor="role-desc" className="block text-sm font-medium text-[#0f172a] mb-1">
                Description
              </label>
              <textarea
                id="role-desc"
                {...register("description")}
                rows={3}
                className={IC}
                placeholder="Optional description of what this role can access"
              />
              {errors.description && (
                <p className="mt-1 text-xs text-[#dc2626]">{errors.description.message}</p>
              )}
            </div>

            {/* Page Access */}
            <div>
              <p className="block text-sm font-medium text-[#0f172a] mb-2">
                Page Access <span className="text-[#dc2626]">*</span>
              </p>
              <p className="text-xs text-[#64748b] mb-3">
                Select which pages users with this role can view.
              </p>
              <Controller
                name="pages"
                control={control}
                render={({ field }) => (
                  <div className="rounded-lg border border-[#e2e8f0] p-3 space-y-4">
                    {PAGE_CATEGORIES.map((category) => (
                      <div key={category.label}>
                        <p className="text-[10px] font-semibold uppercase tracking-widest text-[#64748b] mb-2">
                          {category.label}
                        </p>
                        <div className="space-y-1">
                          {category.pages.map((page) => {
                            const checked = field.value.includes(page);
                            return (
                              <label
                                key={page}
                                className="flex items-center gap-3 cursor-pointer rounded-md px-2 py-1.5 hover:bg-[#f8fafc] select-none"
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(e) => {
                                    const next = e.target.checked
                                      ? [...field.value, page]
                                      : field.value.filter((p) => p !== page);
                                    field.onChange(next);
                                  }}
                                  className="h-4 w-4 rounded border-[#cbd5e1] text-[#2447d9] focus:ring-[#2447d9]"
                                />
                                <span className="text-sm text-[#0f172a]">{PAGE_LABELS[page]}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              />
              {errors.pages && (
                <p className="mt-1 text-xs text-[#dc2626]">{errors.pages.message}</p>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-[#e2e8f0] px-6 py-4 flex justify-end gap-3">
            <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" loading={isSubmitting}>
              {isEdit ? "Save Changes" : "Create Role"}
            </Button>
          </div>
        </form>
      </aside>
    </>
  );
}
